import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Prompt, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Dropdown,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MailOutlined,
  MessageOutlined,
  MoreOutlined,
  PlusOutlined,
  SaveOutlined,
  StopOutlined,
  ToolOutlined,
  UnlockOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { shrinkPhoto } from "../workReports/photos";
import {
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatDateTime,
  formatDay,
  formatEuro
} from "../workReports/format";
import {
  addProtocol,
  cancelVisit,
  completeVisit,
  createDefectOffer,
  deletePhoto,
  emailVisit,
  getMeta,
  getVisit,
  invoiceVisits,
  listInstallations,
  photoUrl,
  removeProtocol,
  reopenVisit,
  updateVisit,
  uploadPhoto,
  visitPdfUrl
} from "./api";
import { evaluateItem, evaluateProtocol, linesTotal, parseDecimal } from "./calc";
import {
  RESULT_COLORS,
  VISIT_STATUS_COLORS,
  billingLabels,
  resultLabels,
  typeLabels,
  visitStatusLabels
} from "./labels";
import CompleteDialog from "./CompleteDialog";
import "./maintenance.css";

const DRAFT_PREFIX = "maintenance-visit-draft:";
let seed = 0;
const lineKey = () => `l${Date.now().toString(36)}${(seed += 1)}`;

const readDraft = id => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_PREFIX + id) || "null");
  } catch {
    return null;
  }
};
const writeDraft = (id, draft) => {
  try {
    if (draft) localStorage.setItem(DRAFT_PREFIX + id, JSON.stringify(draft));
    else localStorage.removeItem(DRAFT_PREFIX + id);
  } catch {
    // storage full or blocked: the server copy is still there
  }
};

const formOf = visit => ({
  instrument: { ...(visit.instrument || {}) },
  protocols: visit.protocols.map(p => ({
    _id: p._id,
    remarks: p.remarks || "",
    nextDueOverride: p.nextDueOverride || "",
    items: Object.fromEntries(
      p.items.map(i => [
        i.key,
        { status: i.status || "", value: i.value || "", note: i.note || "" }
      ])
    )
  })),
  lines: (visit.lines || []).map(l => ({ ...l, key: l._id || lineKey() }))
});

const bodyOf = form => ({
  instrument: form.instrument,
  protocols: form.protocols.map(p => ({
    _id: p._id,
    remarks: p.remarks,
    nextDueOverride: p.nextDueOverride,
    items: Object.entries(p.items).map(([key, v]) => ({ key, ...v }))
  })),
  lines: form.lines.map(l => {
    const copy = { ...l };
    delete copy.key;
    return copy;
  })
});

/** Limit as text: "≤ 0,3 Ω", "≥ 1 MΩ", "17 – 25 mbar". */
const limitText = (limit, unit, lng) => {
  const n = value =>
    new Intl.NumberFormat(lng === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 3 }).format(
      value
    );
  const u = unit ? ` ${unit}` : "";
  const min = limit?.min ?? null;
  const max = limit?.max ?? null;
  if (min !== null && max !== null) return `${n(min)} – ${n(max)}${u}`;
  if (min !== null) return `≥ ${n(min)}${u}`;
  if (max !== null) return `≤ ${n(max)}${u}`;
  return "";
};

const EVAL_COLORS = { pass: "success", fail: "error", na: "default", open: "default" };

const ChecklistItem = ({ item, value, readOnly, lng, photos, onChange, onPhoto, photoSrc }) => {
  const { t } = useTranslation("dashboard");
  const [noteOpen, setNoteOpen] = useState(Boolean(value.note));
  const merged = { ...item, ...value };
  const evaluation = evaluateItem(merged);
  const evalLabels = { pass: t("OK"), fail: t("Defect"), na: t("n/a"), open: t("Open") };
  const limit = item.kind === "measure" ? limitText(item.limit, item.unit, lng) : "";
  const invalid = item.kind === "measure" && value.value && parseDecimal(value.value) === null;

  return (
    <div className={`mnt-item mnt-item-${evaluation}`}>
      <div className="mnt-item-head">
        <div className="mnt-item-label">
          {item.label}
          {!item.required && <span className="mnt-optional"> ({t("optional")})</span>}
          {item.hint && <div className="mnt-small">{item.hint}</div>}
        </div>
        {evaluation !== "open" && (
          <Tag color={EVAL_COLORS[evaluation]}>{evalLabels[evaluation]}</Tag>
        )}
      </div>

      {item.kind === "check" && (
        <Radio.Group
          className="mnt-choice"
          buttonStyle="solid"
          size="large"
          disabled={readOnly}
          value={value.status || null}
          onChange={event => onChange({ status: event.target.value })}
        >
          <Radio.Button value="ok">{t("OK")}</Radio.Button>
          <Radio.Button value="not_ok">{t("Defect")}</Radio.Button>
          <Radio.Button value="na">{t("n/a")}</Radio.Button>
        </Radio.Group>
      )}

      {item.kind === "measure" && (
        <div className="mnt-measure">
          <Input
            size="large"
            inputMode="decimal"
            className="mnt-measure-input"
            disabled={readOnly || value.status === "na"}
            status={invalid ? "error" : evaluation === "fail" ? "warning" : undefined}
            value={value.value}
            addonAfter={item.unit || undefined}
            placeholder={limit || t("Value")}
            onChange={event => onChange({ value: event.target.value })}
          />
          {limit && <span className="mnt-small">{t("Limit {{limit}}", { limit })}</span>}
          <Radio.Group
            size="small"
            disabled={readOnly}
            value={value.status || ""}
            onChange={event => onChange({ status: event.target.value })}
          >
            <Radio.Button value="">{t("Measured")}</Radio.Button>
            <Radio.Button value="not_ok">{t("Defect")}</Radio.Button>
            <Radio.Button value="na">{t("n/a")}</Radio.Button>
          </Radio.Group>
        </div>
      )}

      {item.kind === "text" && (
        <Input.TextArea
          disabled={readOnly}
          autoSize={{ minRows: 1, maxRows: 6 }}
          maxLength={2000}
          value={value.value}
          onChange={event => onChange({ value: event.target.value })}
        />
      )}

      {(noteOpen || value.note) && (
        <Input
          className="mnt-top"
          disabled={readOnly}
          maxLength={1000}
          value={value.note}
          placeholder={evaluation === "fail" ? t("Describe the defect") : t("Note")}
          onChange={event => onChange({ note: event.target.value })}
        />
      )}
      {evaluation === "fail" && !value.note && !noteOpen && !readOnly && (
        <Typography.Text type="danger" className="mnt-small">
          {t("Describe the defect so it appears in the protocol.")}
        </Typography.Text>
      )}

      {photos.length > 0 && (
        <Image.PreviewGroup>
          <div className="mnt-thumbs">
            {photos.map(photo => (
              <Image key={photo._id} width={64} height={64} src={photoSrc(photo._id)} alt="" />
            ))}
          </div>
        </Image.PreviewGroup>
      )}

      {!readOnly && (
        <Space className="mnt-item-tools" size={4}>
          {!noteOpen && !value.note && (
            <Button
              size="small"
              type="text"
              icon={<MessageOutlined />}
              onClick={() => setNoteOpen(true)}
            >
              {t("Note")}
            </Button>
          )}
          <Button size="small" type="text" icon={<CameraOutlined />} onClick={onPhoto}>
            {t("Photo")}
          </Button>
        </Space>
      )}
    </div>
  );
};

const VisitExecution = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const photoInput = useRef(null);
  const photoTarget = useRef(null);
  const [meta, setMeta] = useState(null);
  const [visit, setVisit] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [uploading, setUploading] = useState(0);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // reopen | cancel | email | add
  const [dialogValue, setDialogValue] = useState("");
  const [dialogBusy, setDialogBusy] = useState(false);
  const [customerInstallations, setCustomerInstallations] = useState([]);
  const types = typeLabels(t);
  const statuses = visitStatusLabels(t);
  const results = resultLabels(t);
  const billings = billingLabels(t);

  const apply = data => {
    setVisit(data);
    setForm(formOf(data));
    setDirty(false);
  };

  useEffect(() => {
    Promise.all([getMeta(), getVisit(id)])
      .then(([metaRes, visitRes]) => {
        setMeta(metaRes.data);
        apply(visitRes.data.data);
        const local = readDraft(id);
        const open = ["planned", "in_progress"].includes(visitRes.data.data.status);
        if (local && open && local.savedAt > Date.parse(visitRes.data.data.updatedAt))
          setDraft(local);
        else if (local) writeDraft(id, null);
      })
      .catch(error => {
        generalErrorAlert(error);
        history.push("/user/maintenance?tab=visits");
      });
  }, [id]);

  // Unsaved values stay on the device until saved (cellars rarely have a signal)
  useEffect(() => {
    if (dirty && form) writeDraft(id, { form, savedAt: Date.now() });
  }, [dirty, form]);

  const readOnly =
    !visit || !["planned", "in_progress"].includes(visit.status) || !meta?.can.update;

  const evaluations = useMemo(() => {
    if (!visit || !form) return [];
    return visit.protocols.map((p, index) => {
      const values = form.protocols[index]?.items || {};
      const evaluation = evaluateProtocol({
        items: p.items.map(item => ({ ...item, ...(values[item.key] || {}) }))
      });
      return { _id: p._id, name: p.snapshot.name, ...evaluation };
    });
  }, [visit, form]);

  const totals = useMemo(
    () => (form ? linesTotal(form.lines, visit?.taxRate) : null),
    [form, visit?.taxRate]
  );

  const update = updater => {
    setForm(prev => updater(prev));
    setDirty(true);
  };
  const setItem = (index, key, patch) =>
    update(prev => ({
      ...prev,
      protocols: prev.protocols.map((p, i) =>
        i === index ? { ...p, items: { ...p.items, [key]: { ...p.items[key], ...patch } } } : p
      )
    }));
  const setProtocol = (index, patch) =>
    update(prev => ({
      ...prev,
      protocols: prev.protocols.map((p, i) => (i === index ? { ...p, ...patch } : p))
    }));
  const setLine = (key, patch) =>
    update(prev => ({
      ...prev,
      lines: prev.lines.map(l => (l.key === key ? { ...l, ...patch } : l))
    }));

  const save = async ({ quiet = false } = {}) => {
    setSaving(true);
    try {
      const res = await updateVisit(id, bodyOf(form));
      apply(res.data.data);
      writeDraft(id, null);
      if (!quiet) Success(t("Saved"));
      return res.data.data;
    } catch (error) {
      generalErrorAlert(error);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const choosePhoto = (protocol, itemKey) => {
    photoTarget.current = { protocol, itemKey };
    photoInput.current?.click();
  };

  const upload = async event => {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (!files.length) return;
    if (dirty && !(await save({ quiet: true }))) return;
    for (const file of files) {
      setUploading(n => n + 1);
      try {
        const { blob } = await shrinkPhoto(file);
        const res = await uploadPhoto(id, blob, photoTarget.current || {});
        setVisit(res.data.data);
      } catch (error) {
        generalErrorAlert(error);
      } finally {
        setUploading(n => n - 1);
      }
    }
  };

  const removePhoto = async photoId => {
    try {
      setVisit((await deletePhoto(id, photoId)).data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const openComplete = async () => {
    if (dirty && !(await save({ quiet: true }))) return;
    const incomplete = evaluations.filter(e => e.result === "incomplete");
    if (incomplete.length) {
      Modal.warning({
        rootClassName: "maintenance-page",
        title: t("Some required checks are still open."),
        content: incomplete
          .map(e => `${e.name}: ${e.missing.map(i => i.label).join(", ")}`)
          .join("\n")
      });
      return;
    }
    setCompleteOpen(true);
  };

  const complete = async body => {
    try {
      const res = await completeVisit(id, body);
      apply(res.data.data);
      writeDraft(id, null);
      setCompleteOpen(false);
      const warnings = res.data.warnings || [];
      if (warnings.includes("PDF_NOT_ARCHIVED")) {
        Modal.warning({
          rootClassName: "maintenance-page",
          title: t("Completed, but the PDF could not be created."),
          content: t("Open the PDF later to archive it.")
        });
      } else {
        Success(
          res.data.emailedTo
            ? t("Protocol completed and sent to {{email}}", { email: res.data.emailedTo })
            : t("Protocol completed")
        );
      }
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const openAdd = async () => {
    setDialog("add");
    setDialogValue("");
    try {
      const res = await listInstallations({ client: visit.client });
      const used = new Set(visit.protocols.map(p => String(p.installation)));
      setCustomerInstallations(res.data.data.filter(i => !used.has(String(i._id))));
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const runDialog = async () => {
    setDialogBusy(true);
    try {
      if (dialog === "reopen") apply((await reopenVisit(id, { reason: dialogValue })).data.data);
      if (dialog === "cancel") apply((await cancelVisit(id, { reason: dialogValue })).data.data);
      if (dialog === "email") {
        const res = await emailVisit(id, { to: dialogValue });
        Success(t("Sent to {{email}}", { email: res.data.sentTo }));
      }
      if (dialog === "add") {
        if (dirty && !(await save({ quiet: true }))) return;
        apply((await addProtocol(id, { installation: dialogValue })).data.data);
      }
      setDialog(null);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setDialogBusy(false);
    }
  };

  const dropProtocol = async protocolId => {
    try {
      if (dirty && !(await save({ quiet: true }))) return;
      apply((await removeProtocol(id, protocolId)).data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const defectOffer = async () => {
    try {
      const res = await createDefectOffer(id);
      Success(t("Draft offer created: {{title}}", { title: res.data.offer.title }));
      apply((await getVisit(id)).data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const invoice = async () => {
    try {
      const res = await invoiceVisits([id]);
      Success(t("Draft invoice created. Check it and send it from Invoices."));
      history.push("/user/invoices", { highlight: res.data.invoice._id });
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  if (!visit || !form) {
    return (
      <div className="maintenance-page">
        <Skeleton active />
      </div>
    );
  }

  const client = visit.clientInfo;
  const defects = evaluations.reduce((sum, e) => sum + e.defects.length, 0);
  const technicianName = meta?.me ? meta.employees.find(e => e._id === meta.me)?.name || "" : "";
  const photosOf = (protocolId, itemKey) =>
    (visit.photos || []).filter(p => p.protocol === protocolId && p.itemKey === itemKey);
  const generalPhotos = (visit.photos || []).filter(p => !p.itemKey);

  const menu = [
    visit.status === "completed" && {
      key: "email",
      icon: <MailOutlined />,
      label: t("Email protocol"),
      onClick: () => {
        setDialog("email");
        setDialogValue(client?.email || "");
      }
    },
    visit.status === "completed" &&
      meta?.can.update &&
      visit.billing !== "invoiced" && {
        key: "reopen",
        icon: <UnlockOutlined />,
        label: t("Reopen"),
        onClick: () => {
          setDialog("reopen");
          setDialogValue("");
        }
      },
    !readOnly && {
      key: "cancel",
      icon: <StopOutlined />,
      danger: true,
      label: t("Cancel visit"),
      onClick: () => {
        setDialog("cancel");
        setDialogValue("");
      }
    }
  ].filter(Boolean);

  return (
    <div className="maintenance-page mnt-visit">
      <Prompt when={dirty} message={t("You have unsaved changes. Leave anyway?")} />
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={upload}
      />

      <div className="mnt-visit-head">
        <Link to="/user/maintenance?tab=visits">
          <ArrowLeftOutlined /> {t("Maintenance")}
        </Link>
        <Space wrap className="mnt-visit-title">
          <Typography.Title level={4}>{visit.number}</Typography.Title>
          <Tag color={VISIT_STATUS_COLORS[visit.status]}>{statuses[visit.status]}</Tag>
          {visit.status === "completed" && <Tag>{billings[visit.billing]}</Tag>}
        </Space>
        <Space wrap>
          <Button icon={<FilePdfOutlined />} href={visitPdfUrl(id, lng)} target="_blank">
            {t("PDF")}
          </Button>
          {menu.length > 0 && (
            <Dropdown menu={{ items: menu }} trigger={["click"]}>
              <Button aria-label={t("More")} icon={<MoreOutlined />} />
            </Dropdown>
          )}
        </Space>
      </div>

      {draft && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t("There are unsaved entries from {{time}} on this device.", {
            time: formatDateTime(draft.savedAt, lng)
          })}
          action={
            <Space>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  setForm(draft.form);
                  setDirty(true);
                  setDraft(null);
                }}
              >
                {t("Restore")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  writeDraft(id, null);
                  setDraft(null);
                }}
              >
                {t("Discard")}
              </Button>
            </Space>
          }
        />
      )}

      <Card size="small" className="mb-3">
        <Descriptions size="small" column={{ xs: 1, md: 2 }}>
          <Descriptions.Item label={t("Customer")}>
            {[client?.name, client?.branch].filter(Boolean).join(" · ")}
          </Descriptions.Item>
          <Descriptions.Item label={t("Site")}>
            {visit.siteAddress ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(visit.siteAddress)}`}
                target="_blank"
                rel="noreferrer"
              >
                <EnvironmentOutlined /> {visit.siteAddress}
              </a>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={visit.status === "completed" ? t("Inspected on") : t("Planned for")}
          >
            {formatDay(visit.date || visit.plannedDate)}
          </Descriptions.Item>
          {visit.dueDate && (
            <Descriptions.Item label={t("Due on")}>{formatDay(visit.dueDate)}</Descriptions.Item>
          )}
          {visit.contractInfo && (
            <Descriptions.Item label={t("Contract")}>{visit.contractInfo.number}</Descriptions.Item>
          )}
          {visit.appointmentInfo && (
            <Descriptions.Item label={t("Appointment")}>
              {visit.appointmentInfo.confirmed && visit.appointmentInfo.start
                ? formatDateTime(visit.appointmentInfo.start, lng)
                : t("Proposed, waiting for the customer")}
            </Descriptions.Item>
          )}
          {visit.invoiceInfo && (
            <Descriptions.Item label={t("Invoice")}>
              {visit.invoiceInfo.invoiceNumber || visit.invoiceInfo.title}
            </Descriptions.Item>
          )}
          {visit.offerInfo && (
            <Descriptions.Item label={t("Offer for defects")}>
              {visit.offerInfo.title}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {visit.status === "completed" && (
        <Card size="small" className="mb-3">
          <Space direction="vertical" className="mnt-full">
            <Typography.Text>
              {t("Signed by {{technician}} and {{customer}}", {
                technician: visit.technicianSignature?.name || "",
                customer: visit.customerSignature?.name || `— (${visit.customerAbsentReason})`
              })}{" "}
              · {formatDateTime(visit.completedAt, lng)}
            </Typography.Text>
            <Typography.Text type="secondary" className="mnt-hash">
              SHA-256: {visit.contentHash}
            </Typography.Text>
            <Space wrap>
              {defects > 0 && meta?.can.invoice && (
                <Button
                  icon={<ToolOutlined />}
                  onClick={visit.offerInfo ? () => history.push("/user/offers") : defectOffer}
                >
                  {visit.offerInfo ? t("Open offer for defects") : t("Create offer for defects")}
                </Button>
              )}
              {visit.billing === "open" && meta?.can.invoice && (
                <Button type="primary" icon={<FileTextOutlined />} onClick={invoice}>
                  {t("Create invoice")}
                </Button>
              )}
            </Space>
          </Space>
        </Card>
      )}

      <Collapse
        className="mb-3"
        items={[
          {
            key: "instrument",
            label: visit.instrument?.name
              ? t("Measuring instrument: {{name}}", { name: visit.instrument.name })
              : t("Measuring instrument"),
            children: (
              <Row gutter={[8, 8]}>
                <Col xs={24} md={10}>
                  <Input
                    disabled={readOnly}
                    placeholder={t("Instrument")}
                    value={form.instrument.name}
                    onChange={event =>
                      update(prev => ({
                        ...prev,
                        instrument: { ...prev.instrument, name: event.target.value }
                      }))
                    }
                  />
                </Col>
                <Col xs={12} md={7}>
                  <Input
                    disabled={readOnly}
                    placeholder={t("Serial number")}
                    value={form.instrument.serialNumber}
                    onChange={event =>
                      update(prev => ({
                        ...prev,
                        instrument: { ...prev.instrument, serialNumber: event.target.value }
                      }))
                    }
                  />
                </Col>
                <Col xs={12} md={7}>
                  <DatePicker
                    className="mnt-full"
                    disabled={readOnly}
                    format="DD.MM.YYYY"
                    placeholder={t("Calibrated until")}
                    value={
                      form.instrument.calibratedUntil
                        ? dayjs(form.instrument.calibratedUntil)
                        : null
                    }
                    onChange={value =>
                      update(prev => ({
                        ...prev,
                        instrument: {
                          ...prev.instrument,
                          calibratedUntil: value ? value.format("YYYY-MM-DD") : ""
                        }
                      }))
                    }
                  />
                </Col>
              </Row>
            )
          }
        ]}
      />

      {visit.protocols.map((protocol, index) => {
        const evaluation = evaluations[index];
        const values = form.protocols[index];
        let section = null;
        return (
          <Card
            key={protocol._id}
            className="mb-3 mnt-protocol"
            title={
              <div className="mnt-protocol-title">
                <div>
                  <div>{protocol.snapshot.name}</div>
                  <div className="mnt-small">
                    {[
                      types[protocol.snapshot.type],
                      [protocol.snapshot.manufacturer, protocol.snapshot.model]
                        .filter(Boolean)
                        .join(" "),
                      protocol.snapshot.serialNumber &&
                        `${t("S/N")} ${protocol.snapshot.serialNumber}`,
                      protocol.snapshot.location
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <Tag color={RESULT_COLORS[evaluation.result]}>
                  {evaluation.result === "incomplete"
                    ? t("{{n}} open", { n: evaluation.missing.length })
                    : results[evaluation.result]}
                </Tag>
              </div>
            }
            extra={
              !readOnly &&
              visit.protocols.length > 1 && (
                <Popconfirm
                  title={t("Remove this installation from the visit?")}
                  onConfirm={() => dropProtocol(protocol._id)}
                >
                  <Button type="text" danger aria-label={t("Remove")} icon={<DeleteOutlined />} />
                </Popconfirm>
              )
            }
          >
            <div className="mnt-small mnt-template">
              {protocol.templateTitle}
              {protocol.norm ? ` (${protocol.norm})` : ""}
            </div>
            {protocol.items.map(item => {
              const heading = item.section && item.section !== section ? item.section : null;
              section = item.section || section;
              return (
                <React.Fragment key={item.key}>
                  {heading && <div className="mnt-section-heading">{heading}</div>}
                  <ChecklistItem
                    item={item}
                    value={values.items[item.key] || { status: "", value: "", note: "" }}
                    readOnly={readOnly}
                    lng={lng}
                    photos={photosOf(protocol._id, item.key)}
                    photoSrc={photoId => photoUrl(id, photoId)}
                    onChange={patch => setItem(index, item.key, patch)}
                    onPhoto={() => choosePhoto(protocol._id, item.key)}
                  />
                </React.Fragment>
              );
            })}
            <Row gutter={[8, 8]} className="mnt-top">
              <Col xs={24} md={16}>
                <Input.TextArea
                  disabled={readOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  maxLength={5000}
                  placeholder={t("Remarks on this installation")}
                  value={values.remarks}
                  onChange={event => setProtocol(index, { remarks: event.target.value })}
                />
              </Col>
              <Col xs={24} md={8}>
                {visit.status === "completed" ? (
                  <Typography.Text>
                    {t("Next due")}: {formatDay(protocol.nextDue) || "—"}
                  </Typography.Text>
                ) : (
                  <DatePicker
                    className="mnt-full"
                    disabled={readOnly}
                    format="DD.MM.YYYY"
                    placeholder={t("Shorter next due date")}
                    value={values.nextDueOverride ? dayjs(values.nextDueOverride) : null}
                    onChange={value =>
                      setProtocol(index, {
                        nextDueOverride: value ? value.format("YYYY-MM-DD") : ""
                      })
                    }
                  />
                )}
              </Col>
            </Row>
          </Card>
        );
      })}

      {!readOnly && (
        <Button className="mb-3" icon={<PlusOutlined />} onClick={openAdd}>
          {t("Add installation")}
        </Button>
      )}

      <Card size="small" title={t("Services and material")} className="mb-3">
        {form.lines.map(line => (
          <Row gutter={[8, 8]} key={line.key} className="mnt-line">
            <Col xs={24} md={10}>
              <Input
                disabled={readOnly}
                value={line.description}
                maxLength={500}
                placeholder={t("Description")}
                onChange={event => setLine(line.key, { description: event.target.value })}
              />
            </Col>
            <Col xs={8} md={4}>
              <InputNumber
                className="mnt-full"
                disabled={readOnly}
                min={0}
                precision={3}
                value={line.quantity}
                {...decimalInputProps(lng)}
                onChange={quantity => setLine(line.key, { quantity: quantity || 0 })}
              />
            </Col>
            <Col xs={6} md={3}>
              <Input
                disabled={readOnly}
                value={line.unit}
                maxLength={20}
                placeholder={t("Unit")}
                onChange={event => setLine(line.key, { unit: event.target.value })}
              />
            </Col>
            <Col xs={10} md={5}>
              <InputNumber
                className="mnt-full"
                disabled={readOnly}
                min={0}
                precision={2}
                addonAfter="€"
                value={centsToEuro(line.unitPrice)}
                {...decimalInputProps(lng)}
                onChange={value => setLine(line.key, { unitPrice: euroToCents(value) })}
              />
            </Col>
            <Col xs={24} md={2} className="mnt-line-end">
              {!readOnly && (
                <Button
                  type="text"
                  danger
                  aria-label={t("Remove")}
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    update(prev => ({ ...prev, lines: prev.lines.filter(l => l.key !== line.key) }))
                  }
                />
              )}
            </Col>
          </Row>
        ))}
        {!form.lines.length && (
          <Typography.Paragraph type="secondary">
            {visit.contractInfo?.billingMode === "annual_flat"
              ? t("Covered by the contract's flat fee. Add only extra material or work.")
              : t("No billable lines.")}
          </Typography.Paragraph>
        )}
        {!readOnly && (
          <Button
            icon={<PlusOutlined />}
            onClick={() =>
              update(prev => ({
                ...prev,
                lines: [
                  ...prev.lines,
                  {
                    key: lineKey(),
                    description: "",
                    quantity: 1,
                    unit: "Stk",
                    unitPrice: 0,
                    source: "extra"
                  }
                ]
              }))
            }
          >
            {t("Add line")}
          </Button>
        )}
        {totals?.net > 0 && (
          <div className="mnt-totals">
            {t("Net")} {formatEuro(totals.net, lng)} · {t("VAT")} {formatEuro(totals.tax, lng)} ·{" "}
            <strong>
              {t("Gross")} {formatEuro(totals.gross, lng)}
            </strong>
          </div>
        )}
      </Card>

      <Card
        size="small"
        title={t("Photos")}
        className="mb-3"
        extra={
          !readOnly && (
            <Button
              icon={<CameraOutlined />}
              loading={uploading > 0}
              onClick={() => choosePhoto(null, "")}
            >
              {t("Add photo")}
            </Button>
          )
        }
      >
        {visit.photos?.length ? (
          <Image.PreviewGroup>
            <div className="mnt-thumbs">
              {(generalPhotos.length ? generalPhotos : []).map(photo => (
                <div key={photo._id} className="mnt-thumb">
                  <Image width={96} height={96} src={photoUrl(id, photo._id)} alt="" />
                  {!readOnly && (
                    <Button
                      size="small"
                      type="text"
                      danger
                      aria-label={t("Remove")}
                      icon={<DeleteOutlined />}
                      onClick={() => removePhoto(photo._id)}
                    />
                  )}
                </div>
              ))}
            </div>
            {visit.photos.length > generalPhotos.length && (
              <Typography.Text type="secondary" className="mnt-small">
                {t("{{n}} photos at check points", {
                  n: visit.photos.length - generalPhotos.length
                })}
              </Typography.Text>
            )}
          </Image.PreviewGroup>
        ) : (
          <Typography.Text type="secondary">{t("No photos yet")}</Typography.Text>
        )}
      </Card>

      {!readOnly && (
        <div className="mnt-action-bar">
          <Button
            size="large"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!dirty}
            onClick={() => save()}
          >
            {dirty ? t("Save") : t("Saved")}
          </Button>
          <Button size="large" type="primary" icon={<EditOutlined />} onClick={openComplete}>
            {t("Complete and sign")}
          </Button>
        </div>
      )}

      <CompleteDialog
        open={completeOpen}
        evaluations={evaluations}
        technicianName={technicianName}
        customerEmail={client?.email || ""}
        onCancel={() => setCompleteOpen(false)}
        onComplete={complete}
      />

      <Modal
        rootClassName="maintenance-page"
        open={Boolean(dialog)}
        title={
          {
            reopen: t("Reopen protocol"),
            cancel: t("Cancel visit"),
            email: t("Email protocol"),
            add: t("Add installation")
          }[dialog]
        }
        okText={dialog === "email" ? t("Send") : t("OK")}
        cancelText={t("Cancel")}
        confirmLoading={dialogBusy}
        okButtonProps={{
          disabled: dialog === "add" ? !dialogValue : String(dialogValue).trim().length < 3
        }}
        onOk={runDialog}
        onCancel={() => setDialog(null)}
      >
        {dialog === "add" ? (
          <Select
            className="mnt-full"
            showSearch
            optionFilterProp="label"
            value={dialogValue || undefined}
            placeholder={t("Choose an installation of this customer")}
            options={customerInstallations.map(i => ({
              value: i._id,
              label: [i.name, i.location].filter(Boolean).join(" · ")
            }))}
            onChange={setDialogValue}
          />
        ) : dialog === "email" ? (
          <Input
            type="email"
            inputMode="email"
            value={dialogValue}
            placeholder={t("Email address")}
            onChange={event => setDialogValue(event.target.value)}
          />
        ) : (
          <>
            {dialog === "reopen" && (
              <Typography.Paragraph type="secondary">
                {t(
                  "The signatures are removed and kept in the history. The protocol has to be signed again."
                )}
              </Typography.Paragraph>
            )}
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 5 }}
              maxLength={1000}
              value={dialogValue}
              placeholder={t("Reason")}
              onChange={event => setDialogValue(event.target.value)}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default VisitExecution;
